import React, {useCallback, useEffect, useMemo, useRef} from 'react';
import {Block, Button, Card, FilterDef, FinalTable, FinalTableColumn, FinalTableFiltersDef, Icon, useFinalClientTable} from '@mc/ui-v4';
import {mcClose} from '@mc/react-ui-icons';
import {FeatureId, FeaturePointer, GroupFeaturePointer, LayerId} from '@mc/react-mapio';
import {useMapioContext} from '../../mapioContext';
import {useTableResize} from '../../hooks/useTableResize';
import {CLIENT_ATTRIBUTE_TABLE_FT_KEY} from '../../const/FinalTableKeys';
import {findExternalLayer, getExternalLayerColumnKeys, getExternalLayerRows, ExternalLayerRow} from '../../helpers/ExternalLayerData';
import {coerceExternalLayerRows, ExternalColumnType, getMaxFractionDigits, inferExternalLayerColumnTypes} from '../../helpers/ExternalLayerColumnTypes';
import {formatDate} from '../../helpers/Formatter';
import './AttributeTable.scss';

type Props = {
    layerId: LayerId;
    setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
    /** Provázání s mapou – přesun/výběr featury (stejná funkce jako u serverové tabulky). */
    handleMove: (featurePointers: FeaturePointer[]) => void;
    /** Aktuální výběr na mapě – pro promítnutí do zaškrtnutí řádků. */
    selectedDetailFeature?: GroupFeaturePointer[];
};

/** Bezpečné vykreslení hodnoty atributu – číselníky/objekty převede na čitelný text. */
const renderCellValue = (value: unknown): React.ReactNode => {
    if (value == null) return '';
    if (typeof value === 'object') {
        const obj = value as Record<string, unknown>;
        return (obj.nazev as string) ?? (obj.label as string) ?? JSON.stringify(value);
    }
    if (typeof value === 'boolean') return value ? 'Ano' : 'Ne';
    return String(value);
};

/** Typ filtru odpovídající odhadnutému typu sloupce – u čísel a datumů nabídneme rozsah "od–do". */
const FILTER_TYPE_BY_COLUMN_TYPE: Record<ExternalColumnType, FilterDef['type']> = {
    text: 'text',
    integer: 'integerRange',
    decimal: 'decimalRange',
    date: 'dateRange',
    dateTime: 'dateTimeRange',
    boolean: 'selectBox',
};

const BOOLEAN_FILTER_ITEMS = [
    {id: true, name: 'Ano'},
    {id: false, name: 'Ne'},
];

/**
 * Atributová tabulka pro externí (připojené / exportované) vrstvy.
 *
 * Data se NEnačítají ze serveru – jsou k dispozici klientsky v mapioContextu (GeoJSON properties),
 * proto je tabulka postavena nad `useFinalClientTable`. Tím je tato komponenta zcela oddělená od
 * serverové {@link AttributeTableFinal} a nemůže ovlivnit její chování (žádná regrese).
 */
export const ClientAttributeTableFinal: React.FC<Props> = ({layerId, setIsOpen, handleMove, selectedDetailFeature}) => {
    const mapioContext = useMapioContext();
    const {externalLayers} = mapioContext.value;

    const {tableHeight, handleMouseDown} = useTableResize('.att_table_container');

    const layer = useMemo(() => findExternalLayer(externalLayers, layerId), [externalLayers, layerId]);

    // Pokud vrstva mezitím zmizela (např. změna tématu / odebrání externí vrstvy), tabulku zavřeme.
    useEffect(() => {
        if (!layer) setIsOpen(false);
    }, [layer, setIsOpen]);

    const columnKeys = useMemo<string[]>(() => getExternalLayerColumnKeys(layer), [layer]);

    // Externí vrstvy nemají definici atributů – typ sloupce odhadneme z hodnot a data na něj převedeme,
    // jinak by se čísla řadila jako text ("10" < "9") a filtr by uměl jen fulltext.
    const {rows, columnTypes} = useMemo(() => {
        const rawRows = getExternalLayerRows(layer);
        const types = inferExternalLayerColumnTypes(rawRows, columnKeys);
        return {rows: coerceExternalLayerRows(rawRows, types), columnTypes: types};
    }, [layer, columnKeys]);

    const columns = useMemo<FinalTableColumn<ExternalLayerRow>[]>(
        () =>
            columnKeys.map(key => {
                const type = columnTypes[key] ?? 'text';
                return {
                    accessor: key,
                    header: key,
                    type,
                    sortable: true,
                    groupable: true,
                    ...(type === 'decimal' ? {numberProps: {fractionDigits: getMaxFractionDigits(rows, key)}} : {}),
                    // Čísla si necháme naformátovat tabulkou dle typu sloupce, ostatní vykreslíme sami.
                    ...(type === 'integer' || type === 'decimal'
                        ? {}
                        : {
                              cell: ({row}) =>
                                  type === 'date' || type === 'dateTime' ? formatDate(row[key], type === 'dateTime') : renderCellValue(row[key]),
                          }),
                };
            }),
        [columnKeys, columnTypes, rows],
    );

    const filters = useMemo<FinalTableFiltersDef>(
        () => ({
            filtersDef: columnKeys.map(key => {
                const type = columnTypes[key] ?? 'text';
                return {
                    name: key,
                    type: FILTER_TYPE_BY_COLUMN_TYPE[type],
                    label: key,
                    ...(type === 'boolean' ? {selectBoxProps: {items: BOOLEAN_FILTER_ITEMS}} : {}),
                } as FilterDef;
            }),
        }),
        [columnKeys, columnTypes],
    );

    // settingsKey je per-layer, aby si každá externí vrstva pamatovala vlastní nastavení.
    // Přípona V2 = odhad typů sloupců; zahodí uložené filtry z doby, kdy byly všechny sloupce textové
    // (uložená textová hodnota by v číselném/datumovém filtru nedávala smysl).
    const settingsKey = `${CLIENT_ATTRIBUTE_TABLE_FT_KEY}-SETTINGS-V2-${layerId ?? 'none'}`;
    const ftKey = `${CLIENT_ATTRIBUTE_TABLE_FT_KEY}-${layerId ?? 'none'}`;

    const clientTable = useFinalClientTable<ExternalLayerRow, Record<string, unknown>, FeatureId>(ftKey, rows, {
        settingsKey,
        columns,
        filters,
        columnsFilterable: true,
        checkable: true,
        autoUseLastFilter: true,
    });

    // FeaturePointer pro řádek – mapa zná externí featury pod id externí vrstvy (prop layerId),
    // pod kterým byla vrstva registrována přes loadExternalLayers. layerId v properties featury
    // bývá u importovaných dat null, proto ho nepoužíváme.
    const rowFeaturePointer = useCallback(
        (row: ExternalLayerRow): FeaturePointer => ({
            layerId,
            featureId: row.id as FeatureId,
        }),
        [layerId],
    );

    // Mapa řádků dle featureId pro rychlé párování s výběrem na mapě.
    const rowIdSet = useMemo(() => new Set(rows.map(row => String(row.id))), [rows]);

    // Příznak: zaškrtnutí proběhlo kvůli výběru na mapě → handleMove nesmí výběr na mapě znovu přenastavovat.
    const suppressHandleMoveRef = useRef(false);

    // Mapa → tabulka: výběr na mapě promítneme do zaškrtnutých řádků.
    useEffect(() => {
        const matchedIds = (selectedDetailFeature ?? []).filter(f => rowIdSet.has(String(f.featureId))).map(f => f.featureId as FeatureId);
        const current = clientTable.checkedIds ?? [];
        const same = matchedIds.length === current.length && matchedIds.every(id => current.some(c => String(c) === String(id)));
        if (!same) {
            suppressHandleMoveRef.current = true;
            clientTable.setCheckedIds(matchedIds);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(selectedDetailFeature), rowIdSet]);

    // Tabulka → mapa: zaškrtnuté řádky (checkbox) přeneseme do výběru na mapě.
    useEffect(() => {
        if (suppressHandleMoveRef.current) {
            suppressHandleMoveRef.current = false;
            return;
        }
        const checked = clientTable.checkedIds ?? [];
        const pointers = checked
            .map(id => {
                const index = clientTable.ids?.findIndex(rowId => String(rowId) === String(id)) ?? -1;
                const row = index >= 0 ? clientTable.getRowByIndex(index) : undefined;
                return row ? rowFeaturePointer(row) : {layerId, featureId: id};
            })
            .filter(Boolean) as FeaturePointer[];
        handleMove(pointers);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(clientTable.checkedIds)]);

    return (
        <Card
            className="d-flex flex-column att_table_container"
            style={{height: `${tableHeight}px`}}
            title={layer?.name && layer.name !== 'data' ? layer.name : (layer?.sourceFileName ?? 'Atributová tabulka')}
            extra={<Button style="link" icon={<Icon icon={mcClose} />} onClick={() => setIsOpen(false)} />}
        >
            <div className="resize-handle" onMouseDown={handleMouseDown} />
            <Block>
                <FinalTable<ExternalLayerRow, Record<string, unknown>, FeatureId>
                    className="w-100"
                    finalTableProps={clientTable}
                    highlightId={selectedDetailFeature ? selectedDetailFeature?.[0]?.featureId : null}
                    getRowProps={({row}) => ({
                        onClick: () => {
                            // Klik na řádek = výběr a přesun na featuru v mapě (jako u serverové datové tabulky).
                            if (row) handleMove([rowFeaturePointer(row)]);
                        },
                    })}
                />
            </Block>
        </Card>
    );
};
